import { useState } from 'react';
import { DatePicker, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { CalendarArrowDown, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  downloadDailySiteEngineerReport,
  type DailyReportFormat,
} from '../../../../services/reportService';

/**
 * "Download Daily Report" — replaces the generic ExportMenu for the Manager
 * role. Opens a date-picker modal; the backend generates a Sorigin-branded,
 * manager-scoped Location → Project → Engineer report for that date.
 */
export default function DailyReportButton() {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [busy, setBusy] = useState<DailyReportFormat | null>(null);

  const run = async (format: DailyReportFormat) => {
    setBusy(format);
    try {
      await downloadDailySiteEngineerReport(date.format('YYYY-MM-DD'), format);
      toast.success(`Daily report downloaded — ${date.format('DD MMM YYYY')}`);
      setOpen(false);
    } catch {
      toast.error('Report generation failed — please try again.');
    } finally {
      setBusy(null);
    }
  };

  const actionBtn = (format: DailyReportFormat, label: string, Icon: typeof FileText) => (
    <button
      key={format}
      type="button"
      disabled={busy !== null}
      onClick={() => run(format)}
      className="flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:bg-[#2d5c47] disabled:opacity-60"
      style={{ backgroundColor: '#264f3c', border: '1px solid rgba(11,59,46,0.45)' }}
    >
      {busy === format ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:bg-[#2d5c47]"
        style={{
          backgroundColor: '#264f3c',
          border: '1px solid rgba(11,59,46,0.45)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
        }}
      >
        <CalendarArrowDown className="h-3.5 w-3.5" />
        Download Daily Report
      </button>
      <Modal
        title="Download Daily Report"
        open={open}
        onCancel={() => {
          if (!busy) setOpen(false);
        }}
        footer={
          <div className="flex justify-end gap-2">
            {actionBtn('pdf', 'Download PDF', FileText)}
            {actionBtn('xlsx', 'Download Excel', FileSpreadsheet)}
          </div>
        }
        destroyOnHidden
      >
        <p className="mb-3 text-sm text-[#606060]">Select a date to generate the report.</p>
        <DatePicker
          value={date}
          onChange={(d) => d && setDate(d)}
          allowClear={false}
          disabledDate={(d) => d.isAfter(dayjs(), 'day')}
          format="DD MMM YYYY"
          className="w-full"
        />
        <p className="mt-3 text-xs text-[#8A8A8A]">
          Covers every engineer across all locations &amp; projects for the selected date, grouped
          by location. Dashboard filters don&apos;t apply.
        </p>
      </Modal>
    </>
  );
}
